import { clsx } from 'clsx';
import type { ReactNode } from 'react';

/**
 * The kit's sibling switchers — TabBar, SubTabs, SubRail.
 *
 * Law 3: a sibling switcher is ANCHORED. It is present on every sibling state,
 * never only on the first, so a user can hop between siblings from any of them.
 * Drill-downs never get one — they use a Crumb.
 *
 * Which component to use is decided once, in `2 PLAN — DESKTOP.md` §13.2, and
 * is NOT a judgment call per screen (five different components got proposed
 * before that table existed):
 *
 *   2-6 text labels, main area ....... TabBar   patient file tabs, Bills|Claims
 *   2-4 labels INSIDE a pane ......... SubTabs  Differential|Workup
 *   a switcher that changes the FORM . pills    Directory's Doctor/Lab/Imaging
 *   more than 8 doors ................ SubRail  Settings' thirteen
 *   whole workspaces ................. the Rail itself
 *
 * Pills are not here: the design keeps them as plain Chips, and the form below
 * them is what changes.
 */

export interface TabItem<K extends string = string> {
  key: K;
  label: ReactNode;
  /**
   * Colour the ACTIVE state for this tab specifically. Used by the Notes
   * screen, where UX SPEC §Doctor 03 §2 [1] makes the two tabs "DELIBERATELY
   * distinct because the words are cousins": unsigned is amber with a signature
   * icon, unassigned is indigo with a person icon. "Color + icon + verb keep
   * them apart at a glance" — so the tone is meaning, not decoration.
   */
  tone?: 'brand' | 'warning';
}

interface SwitcherProps<K extends string> {
  items: ReadonlyArray<TabItem<K>>;
  active: K;
  onSelect: (key: K) => void;
  className?: string;
  /** Names the tab set for screen readers, e.g. "Patient file sections". */
  label?: string;
}

/**
 * TabBar — the main-area anchored underline strip (deskkit.tabbar / .livetabs).
 *
 * 40px visual inside a 44px row (§13.1). The kit draws the active tab with
 * `.tb.on` and gives it no click handler; here the active tab stays focusable
 * so keyboard users can tell where they are, but selecting it is a no-op.
 */
export function TabBar<K extends string>({
  items,
  active,
  onSelect,
  className,
  label,
}: SwitcherProps<K>) {
  return (
    <div className={clsx('tabbar', className)} role="tablist" aria-label={label}>
      {items.map((item) => {
        const on = item.key === active;
        return (
          <button
            key={item.key}
            type="button"
            role="tab"
            aria-selected={on}
            className={clsx('tb', on && 'on')}
            onClick={on ? undefined : () => onSelect(item.key)}
          >
            {item.label}
          </button>
        );
      })}
    </div>
  );
}

/**
 * SubTabs — the in-pane strip (deskkit.subtabs, `.tab2`).
 *
 * Distinct from TabBar because a pane is a narrower surface with its own
 * hierarchy: these sit inside the docked pane and switch what it shows about
 * the CURRENT selection, not what the main column lists.
 */
export function SubTabs<K extends string>({
  items,
  active,
  onSelect,
  className,
  label,
}: SwitcherProps<K>) {
  return (
    <div className={clsx('tab2', className)} role="tablist" aria-label={label}>
      {items.map((item) => {
        const on = item.key === active;
        return (
          <button
            key={item.key}
            type="button"
            role="tab"
            aria-selected={on}
            className={clsx('t', on && 'on')}
            onClick={on ? undefined : () => onSelect(item.key)}
          >
            {item.label}
          </button>
        );
      })}
    </div>
  );
}

/**
 * SubRail — a second 200px column for a door set too long for one tab row
 * (deskkit.subrail). Settings' thirteen is the case it exists for.
 *
 * 44px items, same as the SideRail: these are destinations, not filters.
 */
export function SubRail<K extends string>({
  items,
  active,
  onSelect,
  head,
  className,
  label,
}: SwitcherProps<K> & { head?: string }) {
  return (
    <nav className={clsx('subrail', className)} aria-label={label ?? head}>
      {head ? <p className="sr-head">{head}</p> : null}
      {items.map((item) => {
        const on = item.key === active;
        return (
          <button
            key={item.key}
            type="button"
            aria-current={on ? 'page' : undefined}
            className={clsx('sr-item', on && 'on')}
            onClick={on ? undefined : () => onSelect(item.key)}
          >
            <span className="grow">{item.label}</span>
          </button>
        );
      })}
    </nav>
  );
}

/**
 * Segmented — the PHONE's tab row (`maie.css` .subtabs / .st).
 *
 * Distinct from TabBar, which is the desk's underline strip. The phone kit ships
 * `.subtabs` as a chip row; the Doctor canvas overrides it inline into a
 * segmented pill (surface-100 track, surface-0 thumb) for Today | This month,
 * which is the form UX SPEC §Doctor 03 [3] calls "a segmented control on their
 * own row — visually separated from header and content".
 *
 * `pill` reproduces that. Without it you get the kit's plain chip row, which is
 * what other phone screens use.
 */
export function Segmented<K extends string>({
  items,
  active,
  onSelect,
  pill = false,
  className,
  label,
}: SwitcherProps<K> & { pill?: boolean | 'brand' }) {
  /* The desk's brand-filled period switcher is a different control from the
     phone's thumb-on-a-track, so it gets its own class rather than a third
     set of inline overrides. See `.segpill` in kit-extras.css. */
  if (pill === 'brand') {
    return (
      <div className={clsx('segpill', className)} role="tablist" aria-label={label}>
        {items.map((item) => {
          const on = item.key === active;
          return (
            <button
              key={item.key}
              type="button"
              role="tab"
              aria-selected={on}
              className={clsx('s', on && 'on')}
              onClick={on ? undefined : () => onSelect(item.key)}
            >
              {item.label}
            </button>
          );
        })}
      </div>
    );
  }

  const track = pill
    ? {
        background: 'var(--surface-100)',
        borderRadius: 999,
        padding: 3,
        gap: 0,
        margin: '12px 0',
      }
    : undefined;

  return (
    <div className={clsx('subtabs', className)} role="tablist" aria-label={label} style={track}>
      {items.map((item) => {
        const on = item.key === active;
        const toneBg = item.tone ? `var(--${item.tone}-100)` : 'var(--surface-0)';
        const toneFg = item.tone ? `var(--${item.tone}-600)` : 'var(--ink-900)';
        const pillOn = pill && on
          ? {
              flex: 1,
              textAlign: 'center' as const,
              background: toneBg,
              color: toneFg,
              borderColor: toneBg,
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
            }
          : pill
            ? {
                flex: 1,
                textAlign: 'center' as const,
                borderColor: 'transparent',
                background: 'transparent',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
              }
            : undefined;
        return (
          <button
            key={item.key}
            type="button"
            role="tab"
            aria-selected={on}
            className={clsx('st', on && !pill && 'on')}
            style={pillOn}
            onClick={on ? undefined : () => onSelect(item.key)}
          >
            {item.label}
          </button>
        );
      })}
    </div>
  );
}
